// View types and bridge helpers for the report components
// (design 2026-10-02-rapporten-ontwerp.md §4, §5, §7). Every wire type comes
// from `@goblic/shared` (reports/); the aliases below only name them the way
// the components read them.

import {
  REPORT_MAX_SUBMISSION_BYTES,
  ReportSubmissionDataSchema,
  reportByteLength,
  type Report,
  type ReportBridgeSubmitOutcome,
  type ReportRenderResponse,
  type ReportSubmission,
  type ReportVersionSummary as SharedReportVersionSummary,
} from '@goblic/shared'

export type { ReportLastSubmission } from '@goblic/shared'

export {
  REPORT_BRIDGE_GET_LAST_SUBMISSION,
  REPORT_BRIDGE_GET_LAST_SUBMISSION_RESULT,
  REPORT_BRIDGE_HEIGHT,
  REPORT_BRIDGE_SUBMIT,
  REPORT_BRIDGE_SUBMIT_RESULT,
  ReportBridgeChildMessageSchema,
  ReportBridgeGetLastSubmissionMessageSchema,
  ReportBridgeHeightMessageSchema,
  ReportBridgeUnloadMessageSchema,
  ReportBridgeSubmitMessageSchema,
  ReportBridgeSubmitResultMessageSchema,
} from '@goblic/shared'

/** The task's report (`GET /tasks/:taskId/report` → `report`). */
export type ReportView = Report
/** `GET /reports/:reportId/render`; `html` is the complete, CSP'd document. */
export type ReportRenderView = ReportRenderResponse
/** Who made the shown version, and when (the provenance bar). */
export type ReportProvenance = ReportRenderResponse['provenance']
export type ReportVersionSummary = SharedReportVersionSummary
export type ReportSubmissionView = ReportSubmission
/** What `goblic.submit()` resolves with inside the report. */
export type ReportSubmitResult = ReportBridgeSubmitOutcome

/** Same byte limit the API enforces (64 KB). */
export const REPORT_SUBMISSION_MAX_BYTES = REPORT_MAX_SUBMISSION_BYTES

/** True when a version was made by a run (agent), false for a person. */
export function isAgentAuthored(author: {
  runId: string | null
  agentId: string | null
}): boolean {
  return author.runId != null || author.agentId != null
}

/**
 * Normalizes submitted data to plain JSON. postMessage uses structured clone,
 * so a report could send things JSON can't hold (Dates, Maps, BigInt, cyclic
 * graphs); a JSON round trip turns it into exactly what the API will store,
 * and that is what the confirmation dialog shows. The result is then checked
 * against the shared bridge schema (JSON value, ≤ 64 KB).
 */
export function normalizeSubmissionData(
  data: unknown
): { ok: true; data: unknown } | { ok: false; error: string } {
  if (data === undefined) {
    return { ok: false, error: 'Nothing to send: data is missing.' }
  }
  let json: string | undefined
  try {
    json = JSON.stringify(data)
  } catch {
    return { ok: false, error: 'Data must be JSON-serializable.' }
  }
  if (json === undefined) {
    return { ok: false, error: 'Data must be JSON-serializable.' }
  }
  const bytes = reportByteLength(json)
  if (bytes > REPORT_SUBMISSION_MAX_BYTES) {
    return {
      ok: false,
      error: `Data is too large (${Math.ceil(bytes / 1024)} KB; max ${Math.round(REPORT_SUBMISSION_MAX_BYTES / 1024)} KB).`,
    }
  }
  const normalized = JSON.parse(json) as unknown
  const checked = ReportSubmissionDataSchema.safeParse(normalized)
  if (!checked.success) {
    return {
      ok: false,
      error: checked.error.issues[0]?.message ?? 'Data is not valid JSON.',
    }
  }
  return { ok: true, data: checked.data }
}
